import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import * as moment from 'moment';
import { Moment } from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import { RecurrenceConfig } from '../standing-series/standing-series.component';
import { StandingSeriesSaveService, StandingSeriesSaveResult } from '../../../services/standing-series-save.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-event-services-entity',
  templateUrl: './event-services-entity.component.html',
  styleUrls: ['./event-services-entity.component.css']
})
export class EventServicesEntityComponent extends BaseDialogComponent implements OnInit {
  override eckType = "events";
  override bundle = "services";
  override entityId = this._injectedDialogData['data']['EntityID'];

  field_executive: any = null;

  // Recurring series state. The create/generate choreography lives in
  // StandingSeriesSaveService (shared with the lesson dialog); this component
  // only holds UI state and reacts to the service's result.
  //
  // The service is built in ngOnInit from the REST services the base class
  // already injected, NOT via inject()/constructor DI: these dialogs are
  // created through a manual ComponentFactory path and have no own
  // constructor, and on Angular 13 inject() in a field initializer runs
  // outside an injection context (NG0203). It must NOT be a lazy getter either:
  // extractIdFromAutocomplete() is called from a template binding, and a
  // getter that assigns its backing field on first read would mutate component
  // state during change detection (NG0100). ngOnInit runs before the first
  // template evaluation, so the field is ready by the time bindings read it.
  private _seriesSave!: StandingSeriesSaveService;
  isRecurring = false;
  recurrenceConfig: RecurrenceConfig | null = null;
  seriesConflicts: any[] = [];
  pendingSeriesId: number | null = null;

  @ViewChild('picker_field_date_time') picker: any;

  override ngOnInit(): void {
    super.ngOnInit();
    // Build the shared save service from the already-injected REST services.
    // Done here (not a field initializer or template-reachable getter) to stay
    // clear of Angular 13's NG0203 / NG0100 pitfalls; see the field comment.
    this._seriesSave = new StandingSeriesSaveService(
      this._entityRESTService,
      this._drupalRESTService,
    );
  }

  override ngAfterViewInit() {
    // Set default value(s) for services.
    setTimeout(() => {
      this.f.form.patchValue({
        field_status: "113" // "Pending Status"
      });
    });

    // Special handling for loading an attendee.
    if (typeof(this.injectedDialogData?.['data']?.['fieldsData']?.['___field_student']) != 'undefined') {
      this.field_student.push(this.injectedDialogData['data']['fieldsData']['___field_student']);
    }

    // LoggerService.log('this.fieldstudent')
    // LoggerService.log(this.field_student)
  }

  entityType = "attendees";
  entityBundle = "attendance";

  isFormValid(): boolean {
    if (!this.f?.value) return false;

    const requiredFields = {
      field_type: this.f.value.field_type,
      field_executive: this.f.value.field_executive,
      field_status: this.f.value.field_status,
      field_date_and_time: this.f.value.field_date_and_time
    };

    // Check if all required fields have values
    const hasRequiredFields = Object.values(requiredFields).every(value =>
      value !== null && value !== undefined && value !== '');

    // Check if we have at least one attendee
    const hasAttendees = Array.isArray(this.field_student) && this.field_student.length > 0;

    // If in edit mode for attendee, check if the inline form is valid
    if (this.editMode) {
      const inlineForm = this.f.controls['__field_students_inline_form'];
      if (inlineForm && inlineForm.value) {
        const hasRequiredAttendeeFields =
          inlineForm.value.field_student_account &&
          inlineForm.value.field_students;
        return hasRequiredFields && hasAttendees && hasRequiredAttendeeFields;
      }
      // If in edit mode but inline form is not properly initialized, form is not valid
      return false;
    }

    // Also check standing appointment validation
    return hasRequiredFields && hasAttendees && this.isStandingAppointmentValid();
  }

  // Commit a pending inline attendee on Save so users who forget to click
  // Create attendees / Edit Attendee don't silently lose their changes.
  commitPendingAttendee(): void {
    const value = this.f?.form?.controls?.['__field_students_inline_form']?.value;
    if (!value?.field_student_account || !value?.field_students) { return; }

    if (this.editMode) {
      this.studentUpdateSubmit(this.editIndex, { ...value });
    } else {
      this.studentIncrement({ ...value });
    }
  }

  override onSubmit(form: NgForm) {
    this.commitPendingAttendee();

    // Remove the inline form values before submission
    const formValues = { ...form.value };
    delete formValues.__field_students_inline_form;

    if (!this.isFormValid()) {
      this.errorMessage = "Please fill in all required fields and add at least one attendee";
      return;
    }

    // Recurring branch: save a standing_series + generate child events instead
    // of writing a one-off services event.
    if (this.isRecurring && this.recurrenceConfig && this.actionType === 'create') {
      this.saveAsRecurring(form);
      return;
    }

    // Continue with existing submit logic
    super.onSubmit(form);
  }

  studentIncrement(attendee: any) {
    // Convert comma-separated field_students back to array format for saving
    if (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(',')) {
      attendee.field_students = attendee.field_students.split(',');
    }

    // Append type & bundle to model.
    attendee = {
      ...attendee,
      type: 'attendees',
      bundle: 'attendance',
    }

    LoggerService.log('attendee', attendee);

    // If our data binding fails us (API needs to give an array), set as an empty array.
    if (!Array.isArray(this.field_student)) { this.field_student = [] }

    this.field_student.push(attendee);
    this.resetFieldStudent();

    // Force form validation update
    this.f.form.updateValueAndValidity();
  }

  studentRemove = (index: number): void => {
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;
    this.field_student.splice(index, 1);

    this.resetFieldStudent();
  }

  studentUpdateLoad = (index: number): void => {
    this.resetFieldStudent();

    if (!this.field_student || !Array.isArray(this.field_student) || !this.field_student[index]) {
      LoggerService.error('Invalid field_student array or index', {
        fieldStudent: this.field_student,
        index: index
      });
      return;
    }

    const studentAccount = this.field_student[index].field_student_account;
    if (typeof studentAccount !== 'string' || studentAccount.trim() === '') {
      LoggerService.error('Invalid field_student_account', {
        index: index,
        studentAccount: studentAccount
      });
      return;
    }

    // Load the student enrollments again. Don't auto-select during edit mode.
    this.lookupStudentAccountContact({ option: { value: studentAccount } }, false, 'service');
    // Load the student contacts again.
    this.onStudentNameSelectThenLoadEnrollmentName({ option: { value: studentAccount } });

    // Append type & bundle to model.
    this.field_student[index]['type'] = 'attendees';
    this.field_student[index]['bundle'] = 'attendance';

    this.editIndex = index;
    this.editMode = true;

    // Wait for autoCompleteStudentContactOptions to be populated
    const waitForOptions = () => {
      if (this.autoCompleteStudentContactOptions && this.autoCompleteStudentContactOptions.length > 0) {
        // Keep the attendee id so the backend updates the existing attendees
        // entity in place instead of creating a new one on every edit.
        const formValue = { ...this.field_student[index] };

        LoggerService.log('event-services studentUpdateLoad - original field_students:', formValue.field_students);
        LoggerService.log('event-services studentUpdateLoad - typeof field_students:', typeof formValue.field_students);
        LoggerService.log('event-services studentUpdateLoad - autoCompleteStudentContactOptions:', this.autoCompleteStudentContactOptions);

        // Convert array field_students back to comma-separated string for radio button matching
        if (Array.isArray(formValue.field_students)) {
          formValue.field_students = formValue.field_students.join(',');
        }

        LoggerService.log('event-services studentUpdateLoad - converted field_students for matching:', formValue.field_students);

        if (this.f.controls['__field_students_inline_form']) {
          this.f.controls['__field_students_inline_form'].patchValue(formValue);
        } else {
          LoggerService.error('__field_students_inline_form control is not available');
        }
      } else {
        // Options not ready yet, try again in 100ms
        setTimeout(waitForOptions, 100);
      }
    };

    waitForOptions();
  }

  override lookupStudentAccountContact(student_account: { option: { value: string; }; }, selectFirstContact = false, bundle: string = null) {
    LoggerService.log("event-services lookupStudentAccountContact called...", student_account, selectFirstContact, bundle, "editMode:", this.editMode);

    // Fix for console logging undefined error.
    if (student_account == null) { return; }

    let studentID: string;

    // Handle MatAutocompleteSelectedEvent
    if (student_account && typeof student_account === 'object' && student_account.option) {
      studentID = student_account.option.value;
    }
    // Handle direct string value
    else {
      studentID = student_account?.toString() || '';
    }

    studentID = this.regexStudentName(studentID);

    if (studentID == null || studentID == undefined) { return; }

    // Get student account data
    this._entityRESTService.getEntity('student_accounts', 'student_account', studentID)
      .subscribe(data => {
        LoggerService.log('event-services Student account data:', data);

        // Handle contact population (parent logic)
        let contacts = "";
        let contactsID = [];
        let tmp = [];
        let selectOptions = [];

        // Support for couples (multiple contacts)
        if (Array.isArray(data['field_contacts'])) {
          // Filter out archived contacts (field_archived is "1" string when archived)
          const activeContacts = data['field_contacts'].filter(element => element.field_archived !== '1' && element.field_archived !== 1);

          activeContacts.forEach(element => {
            contactsID.push(element.id);
            tmp.push(element.title + ' (' + element.id + ')');
            selectOptions.push({ id: element.id, title: element.title });
          });

          contacts = tmp.join(', ');
          if (activeContacts.length > 1) {
            selectOptions.push({ id: contactsID.toString(), title: contacts });
          }
        } else if (data['field_contacts'] && data['field_contacts'].field_archived !== '1' && data['field_contacts'].field_archived !== 1) {
          // For a single contact (only if not archived)
          selectOptions.push({ id: data['field_contacts'].id, title: data['field_contacts'].title });
          contacts += data['field_contacts'].title + ' (' + data['field_contacts'].id + ')';
        }

        this.autoCompleteStudentContactOptions = selectOptions;
        this.f.form.controls['__field_students_inline_form'].patchValue({
          field_students_contact: contacts
        });

        // Only auto-select if NOT in edit mode
        if (!this.editMode) {
          // For couples, select the combined option by default
          if (selectOptions.length > 1) {
            this.f.form.controls['__field_students_inline_form'].patchValue({
              field_students: contactsID.toString()
            });
            LoggerService.log('event-services Auto-selected couple option:', contactsID.toString());
          }
          // For single contact, select the first contact
          else if (selectOptions.length > 0) {
            const firstContactId = selectOptions[0].id;
            this.f.form.controls['__field_students_inline_form'].patchValue({
              field_students: firstContactId
            });
            LoggerService.log('event-services Auto-selected single contact:', firstContactId);
          }
        } else {
          LoggerService.log('event-services Auto-selection skipped due to edit mode');
        }
      });
  }

  studentUpdateSubmit(_number: any, attendee: any) {
    // Convert comma-separated field_students back to array format for saving
    if (attendee.field_students && typeof attendee.field_students === 'string' && attendee.field_students.includes(',')) {
      attendee.field_students = attendee.field_students.split(',');
    }

    this.field_student[this.editIndex] = attendee;
    this.f.form.controls['__field_students_inline_form'].reset();
    this.editMode = false;
    this.resetFieldStudent();

    // Force form validation update after updating attendee
    this.f.form.updateValueAndValidity();
  }

  resetFieldStudent() {
    this.enrollmentMessage = "";
    this.autoCompleteStudentContactOptions = null;
    this.enrollmentsForUser = null;
    this.f.form.controls['__field_students_inline_form'].reset();
    this.f.form.controls['__field_students_inline_form'].setErrors(null);

    // Force form validation update
    this.f.form.updateValueAndValidity();
  }

  openDateTimePicker() {
    // Set to current time rounded to nearest 15 minutes
    const currentDate = moment();
    const minutes = Math.floor(currentDate.minutes() / 15) * 15;
    const roundedDate = currentDate.clone()
      .minutes(minutes)
      .seconds(0)
      .milliseconds(0);

    // Update the field and trigger change handlers
    this.field_date_and_time = roundedDate.format('YYYY-MM-DD HH:mm:ss');
    this.onDateChange(roundedDate);
    this.uiUpdateDate(roundedDate);

    // Force change detection if needed
    this._changeDetectorRef.detectChanges();

    // Open the picker
    this.picker.open();
  }

  setCurrentRoundedTime() {
    // Only set current time if no date is already selected. Also check the form value.
    if (!this.field_date_and_time && !this.f.value.field_date_and_time) {
      LoggerService.log('setCurrentRoundedTime??', this.field_date_and_time, this.f.value.field_date_and_time);
      const currentDate = moment();
      const minutes = Math.floor(currentDate.minutes() / 15) * 15;
      const roundedDate = currentDate.clone()
        .minutes(minutes)
        .seconds(0)
        .milliseconds(0);

      // Update the field and trigger change handlers
      this.field_date_and_time = roundedDate.format('YYYY-MM-DD HH:mm:ss');
      this.onDateChange(roundedDate);
      this.uiUpdateDate(roundedDate);

      // Force change detection if needed
      this._changeDetectorRef.detectChanges();
    }
  }

  onPickerClosed(picker: any) {
    // Get the selected value from the picker
    const selectedDate = picker.selected;
    if (selectedDate) {
      const momentDate = moment(selectedDate);
      // Round to nearest 15 minutes
      const minutes = Math.floor(momentDate.minutes() / 15) * 15;
      const roundedDate = momentDate.clone()
        .minutes(minutes)
        .seconds(0)
        .milliseconds(0);

      // Update the field and trigger change handlers
      this.field_date_and_time = roundedDate.format('YYYY-MM-DD HH:mm:ss');
      this.onDateChange(roundedDate);
      this.uiUpdateDate(roundedDate);

      this._changeDetectorRef.detectChanges();
    }
  }

  handleDateTimeChange(value: any) {
    if (value) {
      const momentDate = moment(value);
      this.onDateChange(momentDate);
      this.uiUpdateDate(momentDate);
    }
  }

  get sortedServiceTypes() {
    if (!this._taxonomyService.service_type) {
      return [];
    }
    return [...this._taxonomyService.service_type].sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  override onDeleteSubmit(form: any) {
    let id = this._injectedDialogData['data']['EntityID'];

    LoggerService.log('delete service called...', id);

    this.displayProgressSpinner(true);
    this._entityRESTService.deleteEntity(this.eckType, this.bundle, id)
      .subscribe(data => {
        // Only close the current dialog, not all dialogs
        this.closeDialog();

        this.displayProgressSpinner(false);
      },
        error => this.handleError(error)
      );
  }

  // --- Recurring series methods (mirrors event-lesson-entity) ---

  onRecurrenceChanged(config: RecurrenceConfig | null): void {
    this.isRecurring = !!config;
    this.recurrenceConfig = config;
    this.seriesConflicts = [];
  }

  /**
   * Template helper for the <app-standing-series> studentAccountId binding.
   * Self-contained (not delegated to _seriesSave) because this runs during
   * change detection, before/independent of when the service field is built.
   */
  extractIdFromAutocomplete(value: string): string {
    if (!value) return '';
    if (!isNaN(+value)) return value;
    const match = /\(([^)]+)\)(?!.*\()/.exec(value);
    return match ? match[1] : value;
  }

  /** Assemble the bundle-specific save options for StandingSeriesSaveService. */
  private buildSeriesSaveOptions(form: NgForm) {
    const startDateObj = this.field_date_and_time ? new Date(this.field_date_and_time) : new Date();
    const startDate = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;
    const attendee = this.field_student?.[0] || {};
    return {
      // Services store the operator in field_executive; the series carries it
      // as field_instructor and the backend maps it back per bundle.
      bundle: 'services' as const,
      noun: 'services',
      operatorId: this.field_executive || '',
      includeEnrollment: false,
      config: this.recurrenceConfig!,
      attendee,
      studentId: this.extractIdFromAutocomplete(attendee.field_student_account || ''),
      studioId: this._authService.selectedStudio?.id,
      time: this.field_date_and_time ? new Date(this.field_date_and_time).toTimeString().substring(0, 5) : '12:00',
      startDate,
      duration: form?.value?.field_duration || '00:45',
      typeId: form?.value?.field_type || '',
      notes: form?.value?.field_notes || '',
    };
  }

  /** React to a save/generate result with the appropriate UI. */
  private applyRecurringResult(result: StandingSeriesSaveResult): void {
    if (result.kind === 'conflicts') {
      this.pendingSeriesId = result.seriesId;
      this.seriesConflicts = result.conflicts;
      this.isSaving = false;
    } else if (result.kind === 'created') {
      const msg = result.skipped > 0
        ? `Created ${result.count} recurring services (${result.skipped} skipped).`
        : `Created ${result.count} recurring services.`;
      this._snackBar.open(msg, 'OK', { duration: 5000 });
      this.closeDialog(result.data);
    } else {
      this.isSaving = false;
      this.errorMessage = result.message;
    }
  }

  private saveAsRecurring(form: NgForm): void {
    if (this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];
    // A re-save while conflicts are showing starts a fresh series, so retire
    // the one still pending from the previous attempt.
    if (this.pendingSeriesId) {
      this._seriesSave.cancel(this.pendingSeriesId).subscribe(() => {}, () => {});
      this.pendingSeriesId = null;
    }
    this._seriesSave.createAndGenerate(this.buildSeriesSaveOptions(form))
      .subscribe(result => this.applyRecurringResult(result));
  }

  generateIgnoringConflicts(): void {
    if (!this.pendingSeriesId || this.isSaving) return;
    this.isSaving = true;
    this.seriesConflicts = [];
    this._seriesSave.generateIgnoringConflicts(this.pendingSeriesId, this.buildSeriesSaveOptions(this.f))
      .subscribe(result => this.applyRecurringResult(result));
  }

  cancelRecurringSave(): void {
    if (this.pendingSeriesId) {
      this._seriesSave.cancel(this.pendingSeriesId).subscribe(() => {}, () => {});
    }
    this.seriesConflicts = [];
    this.pendingSeriesId = null;
    this.isSaving = false;
  }
}

